import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { getGame, DEFAULT_GAME } from "../lib/games";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { Calendar, Hash, History } from "lucide-react";

export default function GameResult() {
  const { game } = useParams();
  const slug = game || DEFAULT_GAME;
  const g = getGame(slug);
  const [info, setInfo] = useState({ date: "", result: null });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/results/today?game=${slug}`);
        if (active) setInfo(data);
      } catch {}
    };
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, [slug]);

  const num = (v) => v || "XX";

  const pageUrl = `https://nightteerresulttoday.com/result/${slug}`;
  const faqs = [
    { q: `What time is ${g.name} result today?`, a: `${g.name} result is announced daily in two rounds — First Round (F/R) at ${g.fr} and Second Round (S/R) at ${g.sr}. The latest F/R and S/R numbers are shown at the top of this page and update automatically.` },
    { q: `How can I check today's ${g.name} result?`, a: `Simply open this page. Today's ${g.name} F/R and S/R result is displayed live in the result card above. As soon as a new result is published it appears here instantly, without refreshing the whole site.` },
    { q: `Where can I see previous ${g.name} results?`, a: `Tap the "Previous Result" button below the result card to view past ${g.name} results date-wise, with the newest dates shown first.` },
  ];
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: `${g.name} Result Today`,
        url: pageUrl,
        description: `Check ${g.name} result today with live FR and SR numbers, common numbers and previous results.`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://nightteerresulttoday.com/" },
          { "@type": "ListItem", position: 2, name: `${g.name} Result`, item: pageUrl },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title={`${g.name} Result Today | Live FR SR Result`}
        description={`Check ${g.name} result today with the latest FR and SR numbers. Live daily updates, common numbers and previous results for ${g.name}.`}
        path={`/result/${slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonld) }} />
      <div className="home-poppins max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <h1 className="game-name font-oswald text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 text-center">
          {g.name} Result
        </h1>
        <div className="mx-auto mt-2 mb-5 h-1 w-20 rounded-full bg-red-600" />

        <div data-testid="game-result-card" className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.08)] overflow-hidden">
          <div className="flex items-center justify-between gap-3 bg-red-600 px-4 sm:px-5 py-3">
            <div className="flex items-center gap-2 text-white font-semibold text-sm sm:text-base">
              <Calendar size={18} className="shrink-0" />
              <span>Date: {info.date || "--"}</span>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-red-600">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600"></span>
              </span>
              Live Result
            </span>
          </div>
          <div className="bg-slate-900 py-2.5 text-center">
            <span className="font-oswald text-lg sm:text-xl font-bold tracking-wide text-white">{g.name}</span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-200 border-b border-slate-200 bg-slate-50">
            <div className="py-3 text-center text-sm font-semibold text-slate-700">F/R <span className="text-slate-400">·</span> {g.fr}</div>
            <div className="py-3 text-center text-sm font-semibold text-slate-700">S/R <span className="text-slate-400">·</span> {g.sr}</div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-200">
            <div className="flex items-center justify-center py-7">
              <span data-testid="game-fr-number" className="font-oswald font-bold leading-none text-red-600 text-6xl sm:text-7xl tracking-tighter">{num(info.result?.fr)}</span>
            </div>
            <div className="flex items-center justify-center py-7">
              <span data-testid="game-sr-number" className="font-oswald font-bold leading-none text-red-600 text-6xl sm:text-7xl tracking-tighter">{num(info.result?.sr)}</span>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Link to={`/previous-result/${slug}`} data-testid="game-result-previous" className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 font-semibold text-slate-800 shadow-sm hover:shadow-md transition-shadow">
            <History size={18} className="text-red-600" /> Previous Result
          </Link>
          <Link to={`/common-number/${slug}`} data-testid="game-result-common" className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 font-semibold text-slate-800 shadow-sm hover:shadow-md transition-shadow">
            <Hash size={18} className="text-red-600" /> Common Number
          </Link>
        </div>

        {/* Unique per-game content for SEO */}
        <section className="mt-9" data-testid="game-about">
          <h2 className="font-oswald text-2xl font-bold tracking-tight text-slate-900 mb-3">About {g.name} Result</h2>
          <div className="space-y-4 text-slate-600 leading-relaxed text-[15px]">
            <p>
              {g.name} is a popular evening Teer game. The result is declared daily in two rounds — the First Round
              (F/R) at <span className="font-semibold text-slate-800">{g.fr}</span> and the Second Round (S/R) at
              <span className="font-semibold text-slate-800"> {g.sr}</span>. On this page you will always find the
              latest {g.name} result today displayed live at the top, updated automatically as soon as a new result is
              published.
            </p>
            <p>
              Along with today's {g.name} F/R and S/R numbers, you can browse the {g.name}{" "}
              <Link to={`/previous-result/${slug}`} className="font-semibold text-red-600 underline">previous results</Link>{" "}
              date-wise and check the {g.name}{" "}
              <Link to={`/common-number/${slug}`} className="font-semibold text-red-600 underline">common numbers</Link>.
              All numbers on this page are for informational and entertainment purposes only.
            </p>
          </div>
        </section>

        {/* Per-game FAQ */}
        <section className="mt-8" data-testid="game-faq">
          <h2 className="font-oswald text-2xl font-bold tracking-tight text-slate-900 mb-3">{g.name} FAQs</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="font-semibold text-slate-900 text-[15px]">{f.q}</p>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
